import { useAtom } from "@dbeining/react-atom";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { userAtom } from "../../atoms";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import type { ICustomerListRow, ISiteOption } from "../../customTypings/customer/Customer";
import { sitesService } from "../../services/sites/sites.service";
import { customersService } from "../../services/customers/customers.service";
import { addEntityActionLabel, buildRecordLifecycleConfirmProps, EntityLabels, formatPostalAddress, getUserFacingErrorMessage, ListPageTitles, logErrorForDev, recordLifecycleListConfirmBodies, SectionLabels, showToastSuccess } from "../../utils";
import {
    buildCustomerAdminPagedListActions,
    canMutateCustomers,
    getCustomerAdminActionSuccessMessage,
    type CustomerAdminConfirmKind,
} from "./customerAdminListActions";

type CustomerRow = { id: string; name: string; siteId: string };
type ConfirmKind = CustomerAdminConfirmKind | null;

function formatMainLocationAddressSummary(item: {
    addressLine1?: string | null;
    town?: string | null;
    county?: string | null;
    postcode?: string | null;
}): string {
    return formatPostalAddress({
        addressLine1: item.addressLine1,
        town: item.town,
        county: item.county,
        postcode: item.postcode,
    });
}

const CustomersListPage = () => {
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateCustomers(roleNames);

    const [sites, setSites] = useState<ISiteOption[]>([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const [sitesError, setSitesError] = useState<string | null>(null);
    const showSiteColumn = useMemo(() => sites.length > 1, [sites.length]);

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmCustomerId, setConfirmCustomerId] = useState<string | null>(null);
    const [confirmCustomerSiteId, setConfirmCustomerSiteId] = useState<string | null>(null);
    const [confirmCustomerLabel, setConfirmCustomerLabel] = useState("");

    const singleSiteId = sites.length === 1 ? sites[0].id : null;

    useEffect(() => {
        sitesService
            .listForOrganisation()
            .then((list) => {
                setSites(list);
                setSitesError(null);
            })
            .catch((e: unknown) => {
                logErrorForDev("CustomersListPage.sites", e);
                setSitesError(getUserFacingErrorMessage(e, "Could not load sites."));
            })
            .finally(() => setSitesLoading(false));
    }, []);

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmCustomerId(null);
        setConfirmCustomerSiteId(null);
        setConfirmCustomerLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmCustomerId || !confirmKind || !confirmCustomerSiteId) return;
        const id = confirmCustomerId;
        const siteId = confirmCustomerSiteId;
        const op =
            confirmKind === "disable"
                ? customersService.disable(siteId, id)
                : confirmKind === "enable"
                  ? customersService.enable(siteId, id)
                  : confirmKind === "softDelete"
                    ? customersService.softDelete(siteId, id)
                    : customersService.restore(siteId, id);
        op.then(() => {
            showToastSuccess(getCustomerAdminActionSuccessMessage(confirmKind, confirmCustomerLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(typeof e === "string" ? e : "That action couldn't be completed. Please try again.");
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: CustomerRow) => {
        setConfirmKind(kind);
        setConfirmCustomerId(item.id);
        setConfirmCustomerSiteId(item.siteId);
        setConfirmCustomerLabel(item.name);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.customer,
                confirmCustomerLabel,
                recordLifecycleListConfirmBodies(confirmCustomerLabel)
            ),
        [confirmKind, confirmCustomerLabel]
    );

    const columns: IColumn[] = useMemo(() => {
        const base: IColumn[] = [];
        if (showSiteColumn) {
            base.push({ member: "siteName", renderAs: ColumnRender.text, title: "Site", width: 160 } as IColumn);
        }
        base.push(
            { member: "name", renderAs: ColumnRender.text, title: "Name", width: 200 } as IColumn,
            {
                member: "mainAddress",
                renderAs: ColumnRender.customRenderer,
                title: "Main Address",
                width: 260,
                customRenderer: (item: ICustomerListRow) => (
                    <td className="text-start text-muted small">{formatMainLocationAddressSummary(item) || "-"}</td>
                ),
            } as IColumn,
            { member: "isEnabled", renderAs: ColumnRender.checkCross, title: "Enabled", width: 90, align: "center" } as IColumn
        );
        return base;
    }, [showSiteColumn]);

    const siteFilterOptions = useMemo(() => {
        const opts: Record<string, string | null> = { "All sites": null };
        for (const s of sites) {
            opts[s.siteName] = s.id;
        }
        return opts;
    }, [sites]);

    const filters: IFilter[] = useMemo(() => {
        const list: IFilter[] = [];
        if (sites.length > 1) {
            list.push({
                title: "Site",
                member: "siteId",
                type: FilterType.select,
                options: siteFilterOptions,
                defaultValue: null,
            } as IFilter);
        }
        list.push(
            { title: "Name", member: "nameSearch", type: FilterType.text } as IFilter,
            { title: "Main Address", member: "mainAddressSearch", type: FilterType.text } as IFilter,
            {
                title: "Enabled",
                member: "isEnabled",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter
        );
        return list;
    }, [sites.length, siteFilterOptions]);

    const staticFilterValues = useMemo(() => {
        if (sites.length === 1 && singleSiteId) {
            return { siteId: singleSiteId };
        }
        return {};
    }, [sites.length, singleSiteId]);

    const itemActions: IPagedListAction[] = useMemo(() => {
        if (!canMutate) return [];
        return [
            {
                text: "Edit",
                onClick: (item: CustomerRow) =>
                    navigate(`/customers/${item.id}`, { state: { siteId: item.siteId } }),
                primary: true,
            } as IPagedListAction,
            ...buildCustomerAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ];
    }, [canMutate, navigate]);

    const headActions: IPagedListAction[] = useMemo(() => {
        if (!canMutate) return [];
        return [
            {
                text: addEntityActionLabel(EntityLabels.customer),
                icon: faPlus,
                onClick: () => navigate("/customers/new"),
                primary: true,
            } as IPagedListAction,
        ];
    }, [canMutate, navigate]);

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.customers}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: SectionLabels.operations, to: "/operations" },
                            { text: ListPageTitles.customers },
                        ]}
                    />
                    <h1>{ListPageTitles.customers}</h1>
                    <p className="text-muted">
                        Site-scoped customer records (name and address). Duplicate names are enforced within each site only.
                    </p>
                    {sitesError && <Alert variant="danger">{sitesError}</Alert>}
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    {sitesLoading ? (
                        <Alert variant="info">Loading sites…</Alert>
                    ) : sites.length === 0 ? (
                        <Alert variant="warning">No sites are available for your account.</Alert>
                    ) : (
                        <PagedList
                            key={showSiteColumn ? "multi-site" : "single-site"}
                            columns={columns}
                            filters={filters}
                            staticFilterValues={staticFilterValues}
                            headActions={headActions}
                            itemActions={itemActions}
                            rowActionsAriaLabel="Customer actions"
                            showDeletedRowIndicator
                            defaultSortBy={showSiteColumn ? "SiteName" : "Name"}
                            defaultSortOrder={SortOrder.ascending}
                            getUrl="/api/customers/all"
                            rowFormatter={() => null}
                            persistanceKey="CustomersListPage"
                            refreshNumber={refreshNumber}
                        />
                    )}
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { CustomersListPage };
